import { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { listClassifieds, listProducts, listVendors } from '@/api/catalog';
import { subscribeCatalogRefresh } from '@/lib/catalog-refresh';

export function PlatformStatsBar() {
  const statsQuery = useQuery({
    queryKey: ['home', 'platformStats'],
    queryFn: async () => {
      const [vendors, products, classifieds] = await Promise.all([
        listVendors(),
        listProducts(),
        listClassifieds(),
      ]);
      const approvedVendors = vendors.filter((v) => (v.approvalStatus ?? 'none') === 'approved');
      const activeClassifieds = classifieds.filter((c) => c.status === 'active');
      const propertyListings = activeClassifieds.filter((c) =>
        (c.category ?? '').toLowerCase().includes('property'),
      );
      return {
        vendors: approvedVendors.length,
        products: products.length,
        properties: propertyListings.length,
        classifieds: activeClassifieds.length,
      };
    },
    staleTime: 5 * 60_000,
  });

  useEffect(() => subscribeCatalogRefresh(() => {
    void statsQuery.refetch();
  }), [statsQuery]);

  const stats = statsQuery.data ?? {
    vendors: 0,
    products: 0,
    properties: 0,
    classifieds: 0,
  };

  const items = [
    { label: 'Active vendors', value: stats.vendors, to: '/grenada/vendors' },
    { label: 'Products listed', value: stats.products, to: '/grenada/shop' },
    { label: 'Properties', value: stats.properties, to: '/grenada/real-estate' },
    { label: 'Classifieds active', value: stats.classifieds, to: '/grenada/classifieds' },
  ];

  return (
    <section className="border-b border-slate-200 bg-white py-8 dark:border-slate-800 dark:bg-slate-900">
      <div className="mx-auto max-w-[1400px] px-4 sm:px-6">
        <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
          {items.map((item) => (
            <Link
              key={item.label}
              to={item.to}
              className="group rounded-2xl border border-slate-200 bg-white p-5 text-center transition hover:-translate-y-0.5 hover:border-brand-200 hover:shadow-md focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-600 dark:border-slate-700 dark:bg-slate-900 dark:hover:border-brand-600"
              aria-label={`${item.label}: ${statsQuery.isLoading ? 'loading' : item.value.toLocaleString('en-GD')}`}
            >
              <p className="min-h-8 text-2xl font-bold tabular-nums text-brand-800 transition group-hover:text-brand-700 dark:text-brand-300 dark:group-hover:text-brand-200">
                {statsQuery.isLoading ? '—' : item.value.toLocaleString('en-GD')}
              </p>
              <p className="mt-1 text-xs font-semibold uppercase tracking-wide text-slate-500 group-hover:text-brand-700 dark:text-slate-400 dark:group-hover:text-brand-400">
                {item.label}
              </p>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}
